/**
 * Rate Contract Selector Component
 * Modal with search, server-side pagination, multi-select for rate contract items
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from '@/services/interceptor';
import DataGrid from '@/modules/utility/ds-wms/components/WmsDataGrid';
import { TableSkeleton } from '@/components/base/Skeleton';
import { WMS_POPUP_HEADER, WMS_POPUP_TITLE, WMS_POPUP_SUBTITLE, WMS_POPUP_CLOSE } from './common/wmsPopupHeader';

export interface RateContractItem {
  rte_cntrct_itm_id: number;
  itm_nm: string;
  itm_spcfcn_tx: string;
  uom_cd: string;
  uom_nm: string;
  uom_grp: string;
  rate_at: number;
  rte_cntrct_id: number;
  rte_cntrct_cd: string;
  rte_cntrct_nm: string;
  rte_cntrct_type_cd: string;
  rte_cntrct_type_nm: string;
  strt_dt: string;
  end_dt: string;
  cntrctr_nm: string;
}

interface RateContractSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (items: RateContractItem[]) => void;
  excludeIds?: number[];
}

// Stable colour per contract type code. The set of codes is data, not something this
// component can know, so the palette is picked by hashing the code rather than by
// matching literals — every code gets a consistent colour without hard-coding any.
const TYPE_BADGES = [
  'bg-blue-50 text-blue-700',
  'bg-amber-50 text-amber-700',
  'bg-teal-50 text-teal-700',
  'bg-violet-50 text-violet-700',
  'bg-rose-50 text-rose-700',
  'bg-emerald-50 text-emerald-700',
];

function typeBadgeClass(code?: string): string {
  if (!code) return 'bg-gray-100 text-gray-500';
  let hash = 0;
  for (let i = 0; i < code.length; i++) hash = (hash * 31 + code.charCodeAt(i)) >>> 0;
  return TYPE_BADGES[hash % TYPE_BADGES.length];
}

export default function RateContractSelector({ isOpen, onClose, onSelect, excludeIds = [] }: RateContractSelectorProps) {
  const [search, setSearch] = useState('');
  const [items, setItems] = useState<RateContractItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState<Map<number, RateContractItem>>(new Map());
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadItems = useCallback(async (searchText: string, pageNum: number) => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('ds-wms/rate-contract-items/picker', {
        params: { search: searchText || undefined, page: pageNum, limit: 30, validFrom: validFrom || undefined, validTo: validTo || undefined }
      });
      setItems(response.data.data || []);
      setTotal(response.data.pagination?.total || 0);
      setTotalPages(response.data.pagination?.totalPages || 0);
    } catch (e) { console.error('Rate contract load failed:', e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => { setPage(1); loadItems(search, 1); }, 300);
    return () => { if (searchTimeout.current) clearTimeout(searchTimeout.current); };
  }, [search, isOpen]);

  useEffect(() => { if (isOpen && page > 1) loadItems(search, page); }, [page]);

  useEffect(() => {
    if (isOpen) { setSearch(''); setSelected(new Map()); setPage(1); loadItems('', 1); }
  }, [isOpen]);

  const toggleSelect = (item: RateContractItem) => {
    if (excludeIds.includes(item.rte_cntrct_itm_id)) return;
    setSelected(prev => {
      const next = new Map(prev);
      if (next.has(item.rte_cntrct_itm_id)) next.delete(item.rte_cntrct_itm_id);
      else next.set(item.rte_cntrct_itm_id, item);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const available = items.filter(i => !excludeIds.includes(i.rte_cntrct_itm_id));
    setSelected(prev => {
      const next = new Map(prev);
      const allSelected = available.every(i => next.has(i.rte_cntrct_itm_id));
      if (allSelected) available.forEach(i => next.delete(i.rte_cntrct_itm_id));
      else available.forEach(i => next.set(i.rte_cntrct_itm_id, i));
      return next;
    });
  };

  const handleConfirm = () => { onSelect(Array.from(selected.values())); onClose(); };

  if (!isOpen) return null;

  const allPageSelected = items.length > 0 && items.filter(i => !excludeIds.includes(i.rte_cntrct_itm_id)).every(i => selected.has(i.rte_cntrct_itm_id));

  const dataWithSelection = items.map((item, idx) => ({
    ...item,
    sno: (page - 1) * 30 + idx + 1,
    _selected: selected.has(item.rte_cntrct_itm_id),
    _excluded: excludeIds.includes(item.rte_cntrct_itm_id)
  }));

  const columns = [
    { field: '_select', columnName: '', columnType: 'text' as const, width: '40px', visible: true, alignment: 'center' as const,
      cellRenderer: (row: any) => (
        <input type="checkbox" checked={row._selected} disabled={row._excluded}
          onChange={() => toggleSelect(row)}
          className={`rounded border-gray-300 text-teal-600 w-4 h-4 ${row._excluded ? 'opacity-30' : 'cursor-pointer'}`} />
      )
    },
    { field: 'sno', columnName: 'S.No', columnType: 'number' as const, width: '50px', visible: true, alignment: 'center' as const },
    { field: 'rte_cntrct_cd', columnName: 'Contract', columnType: 'text' as const, width: '110px', visible: true,
      cellRenderer: (row: any) => (
        <div>
          <span className="font-mono text-[10px] font-semibold text-teal-700">{row.rte_cntrct_cd}</span>
          <p className="text-[9px] text-gray-400 truncate">{row.rte_cntrct_nm}</p>
        </div>
      )
    },
    { field: 'rte_cntrct_itm_id', columnName: 'Item No', columnType: 'number' as const, width: '65px', visible: true, alignment: 'center' as const,
      cellRenderer: (row: any) => <span className="font-mono text-xs text-gray-600">{row.rte_cntrct_itm_id}</span>
    },
    { field: 'itm_nm', columnName: 'Item Name', columnType: 'text' as const, width: '200px', visible: true, filter: true,
      cellRenderer: (row: any) => (
        <div className="min-w-0">
          <p className="text-xs font-medium truncate">{row.itm_nm}</p>
          {row.itm_spcfcn_tx && <p className="text-[10px] text-gray-400 truncate" title={row.itm_spcfcn_tx}>{row.itm_spcfcn_tx}</p>}
        </div>
      )
    },
    { field: 'cntrctr_nm', columnName: 'Contractor', columnType: 'text' as const, width: '140px', visible: true,
      cellRenderer: (row: any) => <span className="text-xs text-gray-600 truncate block">{row.cntrctr_nm || '-'}</span>
    },
    { field: 'uom_grp', columnName: 'Unit Group', columnType: 'text' as const, width: '80px', visible: true,
      cellRenderer: (row: any) => <span className="text-[10px] text-gray-500">{row.uom_grp || '-'}</span>
    },
    { field: 'uom_cd', columnName: 'UOM', columnType: 'text' as const, width: '60px', visible: true, alignment: 'center' as const },
    { field: 'rate_at', columnName: 'Rate (₹)', columnType: 'number' as const, width: '90px', visible: true, alignment: 'right' as const,
      cellRenderer: (row: any) => <span className="font-mono text-xs font-semibold">₹{row.rate_at?.toLocaleString('en-IN')}</span>
    },
    { field: 'rte_cntrct_type_cd', columnName: 'Type', columnType: 'text' as const, width: '70px', visible: true,
      // The colour used to be a MATERIAL/else ternary, which assumed a vocabulary the
      // Rate Contract Types master never defined. Colour is now derived from the code
      // itself, so any code the master carries renders consistently, and the tooltip
      // shows the master's name for the code (empty when the code is not defined there).
      cellRenderer: (row: any) => (
        <span
          title={row.rte_cntrct_type_nm || `${row.rte_cntrct_type_cd || 'Unknown'} — not defined in Rate Contract Types`}
          className={`text-[10px] px-1.5 py-0.5 rounded ${typeBadgeClass(row.rte_cntrct_type_cd)}`}
        >
          {row.rte_cntrct_type_cd || '-'}
        </span>
      )
    },
    { field: 'strt_dt', columnName: 'Valid From', columnType: 'text' as const, width: '85px', visible: true,
      cellRenderer: (row: any) => <span className="text-[10px] text-gray-500">{row.strt_dt || '-'}</span>
    },
    { field: 'end_dt', columnName: 'Valid To', columnType: 'text' as const, width: '85px', visible: true,
      cellRenderer: (row: any) => <span className="text-[10px] text-gray-500">{row.end_dt || '-'}</span>
    },
  ];

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose}></div>
      <div className={`relative bg-white shadow-2xl flex flex-col ${isFullscreen ? 'w-full h-full rounded-none' : 'rounded-xl w-full max-w-5xl max-h-[85vh]'}`}>
        {/* Header */}
        <div className={`${WMS_POPUP_HEADER} ${isFullscreen ? '' : 'rounded-t-xl'}`}>
          <div className="flex items-center gap-2 min-w-0">
            <i className="ri-price-tag-3-line text-base text-white/90"></i>
            <div className="min-w-0">
              <h2 className={WMS_POPUP_TITLE}>Select Rate Contract Items</h2>
              <p className={WMS_POPUP_SUBTITLE}>{total} items available | {selected.size} selected</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setIsFullscreen(!isFullscreen)} className={WMS_POPUP_CLOSE} title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
              <i className={`${isFullscreen ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'} text-base`}></i>
            </button>
            <button onClick={onClose} className={WMS_POPUP_CLOSE}><i className="ri-close-line text-lg"></i></button>
          </div>
        </div>

        {/* Search + Date Filters + Select All */}
        <div className="px-5 py-3 border-b flex items-center gap-2">
          <label className="flex items-center gap-1.5 flex-shrink-0">
            <input type="checkbox" checked={allPageSelected} onChange={toggleSelectAll}
              className="rounded border-gray-300 text-teal-600 w-4 h-4" />
            <span className="text-[10px] text-gray-500">All</span>
          </label>
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by item name, specification, contract code, contractor..."
              className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500" autoFocus />
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <div>
              <label className="text-[9px] text-gray-400 block">Valid From</label>
              <input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)}
                className="border rounded px-2 py-1.5 text-xs w-32" />
            </div>
            <div>
              <label className="text-[9px] text-gray-400 block">Valid To</label>
              <input type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)}
                className="border rounded px-2 py-1.5 text-xs w-32" />
            </div>
            <button onClick={() => { setPage(1); loadItems(search, 1); }}
              className="mt-3 px-3 py-1.5 bg-teal-600 text-white rounded text-xs font-medium hover:bg-teal-700 flex items-center gap-1">
              <i className="ri-filter-line"></i> Filter
            </button>
            {(validFrom || validTo) && (
              <button onClick={() => { setValidFrom(''); setValidTo(''); setPage(1); loadItems(search, 1); }}
                className="mt-3 px-2 py-1.5 text-gray-500 hover:text-gray-700 text-xs" title="Clear dates">
                <i className="ri-close-line"></i>
              </button>
            )}
          </div>
        </div>

        {/* DataGrid */}
        <div className="flex-1 overflow-auto">
          {loading && items.length === 0 ? (
            <TableSkeleton />
          ) : (
            <DataGrid
              variant="embedded"
              dataSource={dataWithSelection}
              columns={columns}
              manualPagination={true}
              pageCount={totalPages}
              gridConfig={{ pagination: false, enableSearchPanel: false, enableColumnResizing: true }}
              onRowClick={(row: any) => !row._excluded && toggleSelect(row)}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t bg-gray-50 rounded-b-xl">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>Page {page} of {totalPages} ({total} items)</span>
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
              className="px-2 py-1 border rounded hover:bg-gray-100 disabled:opacity-40"><i className="ri-arrow-left-s-line"></i></button>
            <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
              className="px-2 py-1 border rounded hover:bg-gray-100 disabled:opacity-40"><i className="ri-arrow-right-s-line"></i></button>
          </div>
          <div className="flex items-center gap-2">
            {selected.size > 0 && (
              <span className="text-sm font-medium text-teal-700 bg-teal-50 px-3 py-1 rounded-full">
                {selected.size} item{selected.size !== 1 ? 's' : ''} selected
              </span>
            )}
            <button onClick={onClose} className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-100">Cancel</button>
            <button onClick={handleConfirm} disabled={selected.size === 0}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-50 flex items-center gap-1.5">
              <i className="ri-check-line"></i> Add {selected.size} Item{selected.size !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
